import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Heart, Baby, Cake, Feather, Instagram, Lock, LayoutDashboard, Palette, MessageSquareHeart, Send } from "lucide-react";
import { THEME_LIST, THEMES } from "@/lib/themes";
import { useAuth } from "@/lib/auth";
import { getContactSettings } from "@/lib/api";
import { waLink, igLink } from "@/lib/contact";

const ICONS = { heart: Heart, sparkles: Sparkles, baby: Baby, cake: Cake, dove: Feather };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } } };

function WhatsAppIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.76-1.66-2.06-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.87 1.22 3.07.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.08 1.75-.72 2-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35z"/>
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm0 18.15h-.01c-1.52 0-3.01-.41-4.3-1.18l-.31-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.37c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.83 2.42a8.19 8.19 0 0 1 2.41 5.82c0 4.54-3.7 8.24-8.24 8.24z"/>
    </svg>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const [contact, setContact] = useState({ instagram: "", whatsapp: "", whatsapp_message: "" });

  useEffect(() => {
    getContactSettings().then(setContact).catch(() => {});
  }, []);

  const igHref = igLink(contact.instagram);
  const waHref = waLink(contact.whatsapp, contact.whatsapp_message);

  const openWa = (customMsg) => {
    const href = customMsg ? waLink(contact.whatsapp, customMsg) : waHref;
    if (href) window.open(href, "_blank", "noopener");
    else toast.info("Muy pronto habilitado — vuelve en un momento 🙌");
  };
  const openIg = () => {
    if (igHref) window.open(igHref, "_blank", "noopener");
    else toast.info("Muy pronto habilitado — vuelve en un momento 🙌");
  };

  const ContactButtons = ({ className = "", size = "" }) => (
    <div className={`flex items-center gap-3 flex-wrap ${className}`}>
      <button onClick={() => openWa()} data-testid="contact-whatsapp-btn" className={`inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white font-medium shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 ${size === "lg" ? "py-4 px-7 text-base" : "py-2.5 px-5 text-sm"}`}>
        <WhatsAppIcon size={size === "lg" ? 20 : 18} /> WhatsApp
      </button>
      <button onClick={openIg} data-testid="contact-instagram-btn" className={`inline-flex items-center gap-2 rounded-full text-white font-medium shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 ${size === "lg" ? "py-4 px-7 text-base" : "py-2.5 px-5 text-sm"}`} style={{ background: "linear-gradient(45deg,#f09433,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888)" }}>
        <Instagram size={size === "lg" ? 20 : 18} /> Instagram
      </button>
    </div>
  );

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-cream">
      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2">
            <span className="headline text-2xl md:text-3xl text-ink">Fernebia</span>
            <span className="script text-warmgold text-xl -ml-1">·mx</span>
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <a href="#temas" className="text-sm text-inksoft hover:text-ink transition-colors" data-testid="nav-temas">Temáticas</a>
            <a href="#como" className="text-sm text-inksoft hover:text-ink transition-colors" data-testid="nav-como">Cómo funciona</a>
            {user?.is_admin ? (
              <Link to="/dashboard" className="text-sm text-inksoft hover:text-ink transition-colors" data-testid="nav-dashboard">
                <LayoutDashboard size={14} className="inline mr-1" /> Mi panel
              </Link>
            ) : (
              <Link to="/login" className="text-sm text-inksoft hover:text-ink transition-colors inline-flex items-center gap-1" data-testid="nav-admin-login">
                <Lock size={13} /> Ingresar como administrador
              </Link>
            )}
            <button onClick={() => openWa()} className="btn-primary" data-testid="nav-contact-cta">
              Contáctanos <ArrowRight size={16} />
            </button>
          </div>
          <button onClick={() => openWa()} className="md:hidden btn-primary py-2 px-4 text-sm" data-testid="nav-contact-cta-mobile">Contáctanos</button>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative pt-32 md:pt-36 pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
          <motion.div className="md:col-span-7" initial="hidden" animate="show" variants={stagger}>
            <motion.p variants={item} className="overline mb-6">Invitaciones digitales · Hecho en México</motion.p>
            <motion.h1 variants={item} className="headline text-5xl sm:text-6xl lg:text-7xl text-ink leading-[1.02] tracking-tight text-balance">
              Nosotros diseñamos tu
              <span className="script text-warmgold text-6xl lg:text-8xl block md:inline md:ml-4">invitación</span>
            </motion.h1>
            <motion.p variants={item} className="mt-8 text-lg text-inksoft max-w-xl leading-relaxed">
              Cuéntanos tu idea y nosotros creamos por ti una invitación digital que enamora al scroll: música, countdown, itinerario, dresscode, RSVP y más. Tú solo nos escribes por WhatsApp o Instagram — del diseño nos encargamos nosotros.
            </motion.p>
            <motion.div variants={item} className="mt-10">
              <ContactButtons size="lg" />
              <p className="text-xs text-inksoft mt-4">Respuesta rápida · Diseño 100% personalizado · Te la entregamos lista para compartir.</p>
            </motion.div>
          </motion.div>

          <div className="md:col-span-5 relative min-h-[420px] md:min-h-[560px]">
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }} className="absolute top-0 right-4 w-56 md:w-72 h-80 md:h-[26rem] arch-shape overflow-hidden shadow-xl">
              <img src={THEMES.baby_reveal.concepts[3].image} alt="baby reveal" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5 }} className="absolute top-24 md:top-28 -left-2 md:left-0 w-44 md:w-56 h-64 md:h-80 arch-shape overflow-hidden shadow-2xl border-4 border-cream">
              <img src={THEMES.boda.concepts[0].image} alt="boda" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.75 }} className="absolute bottom-4 right-16 md:right-32 glass rounded-2xl px-5 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-dustyrose/40 grid place-items-center">
                <Heart size={16} className="text-warmgold" />
              </div>
              <div>
                <p className="text-xs text-inksoft">Diseño a tu medida</p>
                <p className="script text-lg text-ink leading-none">Hecho por nosotros</p>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="mt-20 md:mt-28 border-y border-warmgold/20 py-6 overflow-hidden">
          <div className="flex marquee whitespace-nowrap gap-16 text-2xl md:text-3xl headline text-ink/70">
            {[...Array(2)].map((_, i) => (
              <span key={i} className="flex items-center gap-16 shrink-0">
                <span>Baby Reveal</span><span className="text-warmgold">✻</span>
                <span>Boda</span><span className="text-warmgold">✻</span>
                <span>XV Años</span><span className="text-warmgold">✻</span>
                <span>Bautizo</span><span className="text-warmgold">✻</span>
                <span>Cumpleaños</span><span className="text-warmgold">✻</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* THEMES SHOWCASE */}
      <section id="temas" className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="overline mb-4">Seis temáticas, infinitos moods</p>
              <h2 className="headline text-4xl md:text-5xl text-ink leading-tight max-w-lg">
                Elige la vibra. Nosotros la vestimos <span className="script text-warmgold text-5xl md:text-6xl">de fiesta</span>.
              </h2>
            </div>
            <p className="text-inksoft max-w-sm">Estos son algunos de los estilos que creamos. Dinos cuál te late y lo personalizamos por completo para tu evento.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-6 md:gap-8">
            {THEME_LIST.map((t, i) => {
              const Icon = ICONS[t.icon] || Sparkles;
              const spans = ["md:col-span-4 md:row-span-2", "md:col-span-2 md:row-span-2", "md:col-span-3", "md:col-span-3", "md:col-span-3", "md:col-span-3"];
              return (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                  className={`group relative overflow-hidden rounded-3xl bg-sand ${spans[i]} min-h-[280px] md:min-h-[320px]`}
                  data-testid={`theme-card-${t.id}`}
                >
                  <img src={t.concepts[0].image} alt={t.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                  <div className="relative h-full p-6 md:p-8 flex flex-col justify-between">
                    <div className="inline-flex items-center gap-2 self-start glass rounded-full px-3 py-1.5">
                      <Icon size={14} className="text-warmgold" />
                      <span className="text-xs text-ink uppercase tracking-widest">{t.tagline}</span>
                    </div>
                    <div>
                      <h3 className="headline text-3xl md:text-4xl text-white leading-none">{t.name}</h3>
                      <p className="text-white/80 text-sm mt-2 max-w-xs">{t.description}</p>
                      <button onClick={() => openWa(`Hola 👋 Me encantó la temática "${t.name}" de Fernebia. ¿Me ayudas a diseñar mi invitación?`)} className="mt-5 inline-flex items-center gap-2 text-white text-sm border-b border-white/60 pb-0.5 hover:border-warmgold hover:text-warmgold transition-colors" data-testid={`theme-contact-${t.id}`}>
                        Pídela por WhatsApp <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="como" className="py-24 md:py-32 bg-sand/50 relative overflow-hidden">
        <div className="mx-auto max-w-6xl px-6 md:px-10">
          <p className="overline mb-4 text-center">Tres pasos, cero complicaciones</p>
          <h2 className="headline text-4xl md:text-5xl text-ink text-center mb-20 max-w-3xl mx-auto leading-tight">
            Tú nos cuentas, nosotros la <span className="script text-warmgold text-5xl md:text-6xl">hacemos realidad</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
            {[
              { n: "01", Icon: MessageSquareHeart, t: "Escríbenos", d: "Mándanos un mensaje por WhatsApp o Instagram con tu evento, fecha y la temática que te gusta." },
              { n: "02", Icon: Palette, t: "La diseñamos por ti", d: "Creamos tu invitación a tu estilo: música, countdown, itinerario, dresscode, RSVP y todos los detalles." },
              { n: "03", Icon: Send, t: "Te la entregamos lista", d: "Recibes un link listo para compartir por WhatsApp con tus invitados y recibir sus confirmaciones." },
            ].map((s) => (
              <motion.div key={s.n} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="relative">
                <div className="flex items-center gap-3">
                  <span className="script text-6xl md:text-7xl text-warmgold/60 leading-none">{s.n}</span>
                  <s.Icon size={26} className="text-warmgold" />
                </div>
                <h3 className="headline text-2xl text-ink mt-3">{s.t}</h3>
                <p className="text-inksoft mt-3 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
          <div className="flex justify-center mt-16">
            <ContactButtons size="lg" />
          </div>
        </div>
      </section>

      {/* FEATURES BAND */}
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-6xl px-6 md:px-10">
          <p className="overline text-center mb-6">Todo lo cool en una sola pantalla</p>
          <h2 className="headline text-4xl md:text-5xl text-ink text-center max-w-2xl mx-auto">Incluimos todo para tu <span className="script text-warmgold text-5xl md:text-6xl">invitación soñada</span></h2>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              "Countdown animado", "Música Spotify/Apple", "Dresscode + Pinterest",
              "Itinerario", "Padrinos", "Hashtag oficial",
              "Playlist del DJ", "Libro de recuerdos", "Live streaming",
              "RSVP + WhatsApp", "Kids policy", "Mesa de regalos",
            ].map((f) => (
              <div key={f} className="glass rounded-2xl px-4 py-4 text-center hover:-translate-y-1 transition-transform duration-300">
                <p className="text-ink text-sm">{f}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-4xl px-6 md:px-10 text-center">
          <p className="overline mb-6">Lista para tu evento</p>
          <h2 className="headline text-5xl md:text-6xl text-ink leading-tight">
            Tu momento merece <span className="script text-warmgold text-6xl md:text-7xl block mt-2">algo así</span>
          </h2>
          <p className="text-inksoft mt-8 max-w-lg mx-auto">Escríbenos y creamos por ti una invitación digital única. Te ayudamos a diseñarla de principio a fin — tú solo compártela y recibe las confirmaciones.</p>
          <div className="flex justify-center mt-10">
            <ContactButtons size="lg" />
          </div>
        </div>
      </section>

      <footer className="py-10 border-t border-warmgold/20">
        <div className="mx-auto max-w-7xl px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-inksoft">
          <span>© Fernebia · Hecho con cariño en México</span>
          <div className="flex items-center gap-5">
            <button onClick={openIg} className="hover:text-ink transition-colors inline-flex items-center gap-1" data-testid="footer-instagram"><Instagram size={16} /> Instagram</button>
            <button onClick={() => openWa()} className="hover:text-ink transition-colors inline-flex items-center gap-1" data-testid="footer-whatsapp"><WhatsAppIcon size={16} /> WhatsApp</button>
            <Link to="/login" className="hover:text-ink transition-colors inline-flex items-center gap-1" data-testid="footer-admin-login"><Lock size={13} /> Admin</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
